import { Section } from "./Section";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tags, Plus, Trash2, Hash } from "lucide-react";

const uid = () => "nc-" + Math.random().toString(36).slice(2, 9);

// Generic, reusable naming-convention builder. Each convention has freely-definable
// variables (text / list / number) combined via a pattern like {type}-{number}-{name}.
export function NamingSection({ s, setS }) {
  const { t } = useLang();
  const convs = s.namingConventions || [];
  const setConvs = (next) => setS({ ...s, namingConventions: next });
  const update = (i, patch) => setConvs(convs.map((c, x) => (x === i ? { ...c, ...patch } : c)));

  const addConv = () => setConvs([...convs, {
    id: uid(), label: "Teams", appliesTo: ["team"], pattern: "{type}-{number}-{name}", enforced: false, counter: 1,
    variables: [
      { key: "type", kind: "list", values: ["PRJ", "DEPT", "OPS"] },
      { key: "number", kind: "number", digits: 4 },
      { key: "name", kind: "text" },
    ],
  }]);

  const setVar = (ci, vi, patch) => update(ci, { variables: convs[ci].variables.map((v, x) => (x === vi ? { ...v, ...patch } : v)) });
  const addVar = (ci) => update(ci, { variables: [...convs[ci].variables, { key: "var", kind: "text" }] });
  const rmVar = (ci, vi) => update(ci, { variables: convs[ci].variables.filter((_, x) => x !== vi) });

  return (
    <Section icon={Tags} title={t("set.namingTitle")}>
      <p className="mb-4 text-xs text-zinc-400">{t("set.namingIntro")}</p>
      <div className="space-y-5">
        {convs.map((c, ci) => (
          <div key={c.id} className="rounded-sm border border-zinc-200 p-4" data-testid={`naming-conv-${ci}`}>
            <div className="flex items-center justify-between gap-3">
              <Input value={c.label} onChange={(e) => update(ci, { label: e.target.value })} placeholder={t("set.namingLabel")} data-testid={`naming-label-${ci}`} className="max-w-[220px] rounded-sm border-zinc-300 font-semibold" />
              <button onClick={() => setConvs(convs.filter((_, x) => x !== ci))} data-testid={`naming-remove-${ci}`} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t("set.namingPattern")}</Label>
                <Input value={c.pattern} onChange={(e) => update(ci, { pattern: e.target.value })} data-testid={`naming-pattern-${ci}`} className="rounded-sm border-zinc-300 font-mono text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t("set.namingCounter")}</Label>
                <Input type="number" min={0} value={c.counter ?? 1} onChange={(e) => update(ci, { counter: Number(e.target.value) })} data-testid={`naming-counter-${ci}`} className="w-32 rounded-sm border-zinc-300" />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <Switch checked={(c.appliesTo || []).includes("team")} onCheckedChange={(v) => update(ci, { appliesTo: v ? ["team"] : [] })} data-testid={`naming-team-${ci}`} /> {t("set.namingApplyTeams")}
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <Switch checked={!!c.enforced} onCheckedChange={(v) => update(ci, { enforced: v })} data-testid={`naming-enforced-${ci}`} /> {t("set.namingEnforce")}
              </label>
            </div>
            <div className="mt-4 space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t("set.namingVariables")}</Label>
              {c.variables.map((v, vi) => (
                <div key={vi} className="flex flex-wrap items-center gap-2 rounded-sm bg-zinc-50 p-2" data-testid={`naming-var-${ci}-${vi}`}>
                  <Input value={v.key} onChange={(e) => setVar(ci, vi, { key: e.target.value })} placeholder="key" className="w-28 rounded-sm border-zinc-300 font-mono text-xs" />
                  <Select value={v.kind} onValueChange={(val) => setVar(ci, vi, { kind: val })}>
                    <SelectTrigger className="w-28 rounded-sm border-zinc-300 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">{t("set.namingText")}</SelectItem>
                      <SelectItem value="list">{t("set.namingList")}</SelectItem>
                      <SelectItem value="number">{t("set.namingNumber")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {v.kind === "list" && (
                    <Input value={(v.values || []).join(", ")} onChange={(e) => setVar(ci, vi, { values: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder={t("set.namingValues")} className="min-w-[160px] flex-1 rounded-sm border-zinc-300 text-xs" />
                  )}
                  {v.kind === "number" && (
                    <span className="flex items-center gap-1 text-xs text-zinc-500"><Hash size={12} /> <Input type="number" min={1} max={12} value={v.digits || 1} onChange={(e) => setVar(ci, vi, { digits: Number(e.target.value) })} className="w-16 rounded-sm border-zinc-300 text-xs" /> {t("set.namingDigits")}</span>
                  )}
                  <button onClick={() => rmVar(ci, vi)} className="ml-auto text-zinc-400 hover:text-red-600"><Trash2 size={13} /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addVar(ci)} data-testid={`naming-add-var-${ci}`} className="rounded-sm border-zinc-300 text-xs"><Plus size={13} className="mr-1" /> {t("set.namingAddVar")}</Button>
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={addConv} data-testid="naming-add-convention" className="rounded-sm border-zinc-300"><Plus size={15} className="mr-2" /> {t("set.namingAdd")}</Button>
      </div>
    </Section>
  );
}
